import { API_BASE_URL } from '../config';
import { 
  INITIAL_CATEGORIES, 
  INITIAL_SHIPPING_METHODS, 
  INITIAL_COUPONS, 
  INITIAL_PRODUCTS 
} from './LojaMockData';

class LojaService {
  constructor() {
    this.baseUrl = API_BASE_URL;
    this.endpoints = {
      categorias: `${API_BASE_URL}/core/loja/categorias/`,
      produtos: `${API_BASE_URL}/core/loja/produtos/`,
      produtoDetalhe: (slugOrId) => `${API_BASE_URL}/core/loja/produtos/${slugOrId}/`,
      avaliacoes: `${API_BASE_URL}/core/loja/avaliacoes/`,
      frete: `${API_BASE_URL}/core/loja/frete/calcular/`,
      cupom: `${API_BASE_URL}/core/loja/cupons/validar/`,
      checkout: `${API_BASE_URL}/core/loja/checkout/`,
      pedidoDetalhe: (numero) => `${API_BASE_URL}/core/loja/pedidos/${numero}/`,
      meusPedidos: `${API_BASE_URL}/core/loja/pedidos/meus/`,
      adminKpis: `${API_BASE_URL}/core/loja/admin/kpis/`,
      adminEstoque: `${API_BASE_URL}/core/loja/admin/estoque/`,
      adminPedidos: `${API_BASE_URL}/core/loja/admin/pedidos/`,
      adminCupons: `${API_BASE_URL}/core/loja/admin/cupons/`,
    };
  }

  getHeaders(includeAuth = false) {
    const headers = {
      'Content-Type': 'application/json; charset=UTF-8',
    };
    if (includeAuth) {
      const token = sessionStorage.getItem('brv-user-token');
      if (token) {
        headers['Authorization'] = `Token ${token}`;
      }
    }
    return headers;
  }

  // --- CATEGORIAS ---
  async getCategories() {
    try {
      const res = await fetch(this.endpoints.categorias, {
        method: 'GET',
        headers: this.getHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) return data;
      }
    } catch (e) {
      // Backend fallback
    }
    return INITIAL_CATEGORIES;
  }

  // --- PRODUTOS E CATÁLOGO ---
  async getProducts(filters = {}) {
    const {
      category = 'todos',
      search = '',
      size = null,
      color = null,
      gender = 'Todos',
      minPrice = null,
      maxPrice = null,
      sort = 'relevance'
    } = filters;

    try {
      const queryParams = new URLSearchParams();
      if (category && category !== 'todos') queryParams.append('category', category);
      if (search) queryParams.append('search', search);
      if (size) queryParams.append('size', size);
      if (color) queryParams.append('color', color);
      if (gender && gender !== 'Todos') queryParams.append('gender', gender);
      if (minPrice) queryParams.append('minPrice', minPrice);
      if (maxPrice) queryParams.append('maxPrice', maxPrice);
      if (sort) queryParams.append('sort', sort);

      const res = await fetch(`${this.endpoints.produtos}?${queryParams.toString()}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) return data;
      }
    } catch (e) {
      // Backend fallback
    }

    // Filtragem local resiliente em memória caso a API ainda não tenha os dados populados
    let list = [...INITIAL_PRODUCTS];

    if (category && category !== 'todos') {
      const catSlug = category.replace('cat_', '');
      list = list.filter(p => p.category_id === category || p.category_id === `cat_${catSlug}`);
    }

    if (search && search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter(p => 
        p.name.toLowerCase().includes(q) || 
        p.short_description.toLowerCase().includes(q) ||
        (p.sku && p.sku.toLowerCase().includes(q))
      );
    }

    if (gender && gender !== 'Todos') {
      list = list.filter(p => p.gender === gender || p.gender === 'Unissex');
    }

    if (size) {
      list = list.filter(p => p.variants && p.variants.some(v => v.size === size && v.stock > 0));
    }

    if (color) {
      list = list.filter(p => p.variants && p.variants.some(v => v.color.toLowerCase() === color.toLowerCase() && v.stock > 0));
    }

    if (minPrice !== null && !isNaN(minPrice)) {
      list = list.filter(p => (p.promotional_price || p.price) >= Number(minPrice));
    }

    if (maxPrice !== null && !isNaN(maxPrice)) {
      list = list.filter(p => (p.promotional_price || p.price) <= Number(maxPrice));
    }

    // Ordenação
    switch (sort) {
      case 'price_asc':
        list.sort((a, b) => (a.promotional_price || a.price) - (b.promotional_price || b.price));
        break;
      case 'price_desc':
        list.sort((a, b) => (b.promotional_price || b.price) - (a.promotional_price || a.price));
        break;
      case 'bestsellers':
        list.sort((a, b) => (b.rating_count || 0) - (a.rating_count || 0));
        break;
      case 'newest':
        list.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
        break;
      default: // relevance
        list.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
        break;
    }

    return list;
  }

  async getProductBySlugOrId(slugOrId) {
    try {
      const res = await fetch(this.endpoints.produtoDetalhe(slugOrId), {
        method: 'GET',
        headers: this.getHeaders(),
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (e) {
      // Backend fallback
    }

    const found = INITIAL_PRODUCTS.find(p => p.id === slugOrId || p.slug === slugOrId);
    return found || null;
  }

  // --- COTAÇÃO DE FRETE ---
  async quoteShipping(cep, subtotal = 0) {
    const cleanCep = String(cep || '').replace(/\D/g, '');
    if (cleanCep.length !== 8) return [];

    try {
      const res = await fetch(this.endpoints.frete, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ cep: cleanCep, subtotal }),
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (e) {
      // Backend fallback
    }

    // Cálculo local padrão de alta precisão
    const isSP = cleanCep.startsWith('0');
    return INITIAL_SHIPPING_METHODS.map(m => {
      let cost = m.base_cost;
      let days = m.estimated_days;

      if (!isSP) {
        if (m.code === 'entrega_local') {
          return null; // Apenas grande SP
        }
        if (m.code === 'correios_pac') {
          cost = 28.90;
          days = 7;
        } else if (m.code === 'correios_sedex') {
          cost = 49.90;
          days = 3;
        }
      }

      return {
        ...m,
        cost: Number(cost.toFixed(2)),
        days: days
      };
    }).filter(Boolean);
  }

  // --- VALIDAÇÃO DE CUPOM ---
  async validateCoupon(code, cartSubtotal) {
    if (!code) return { valid: false, error: 'Código de cupom obrigatório.' };

    try {
      const res = await fetch(this.endpoints.cupom, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ code: code.toUpperCase().trim(), subtotal: cartSubtotal }),
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (e) {
      // Backend fallback
    }

    const cleanCode = code.toUpperCase().trim();
    const coupon = INITIAL_COUPONS.find(c => c.code === cleanCode);

    if (!coupon) {
      return { valid: false, error: 'Cupom inválido ou expirado.' };
    }

    if (cartSubtotal < coupon.min_order_amount) {
      return { 
        valid: false, 
        error: `Pedido mínimo de R$ ${coupon.min_order_amount.toFixed(2).replace('.', ',')} para este cupom.` 
      };
    }

    let discount = 0;
    if (coupon.type === 'percentage') {
      discount = (cartSubtotal * coupon.value) / 100;
    } else if (coupon.type === 'fixed') {
      discount = coupon.value;
    } else if (coupon.type === 'free_shipping') {
      discount = 0; // Calculado no frete
    }

    return {
      valid: true,
      coupon: {
        code: coupon.code,
        type: coupon.type,
        value: coupon.value,
        discount: Number(discount.toFixed(2))
      }
    };
  }

  // --- SUBMISSÃO DE PEDIDO / CHECKOUT ---
  async checkout(orderData) {
    try {
      const res = await fetch(this.endpoints.checkout, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify(orderData),
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (e) {
      // Backend fallback
    }

    // Geração de pedido simulado com conformidade total e persistência local
    const orderNumber = `URB-${Math.floor(10000 + Math.random() * 89999)}`;
    const pixCopyPaste = `00020101021226840014br.gov.bcb.pix2562pix.urbrace.com.br/qr/v2/${orderNumber}520400005303986540${orderData.totalAmount.toFixed(2)}5802BR5920URBRACE ESPORTES LTDA6009SAO PAULO62070503***6304${Math.random().toString(16).substring(2, 6).toUpperCase()}`;

    const createdOrder = {
      orderNumber,
      status: orderData.paymentMethod === 'PIX' ? 'aguardando_pagamento' : 'pagamento_aprovado',
      createdAt: new Date().toISOString(),
      customer: orderData.customer,
      shipping: orderData.shipping,
      paymentMethod: orderData.paymentMethod,
      items: orderData.items,
      subtotal: orderData.subtotal,
      shippingCost: orderData.shippingCost,
      discount: orderData.discount || 0,
      totalAmount: orderData.totalAmount,
      pix: orderData.paymentMethod === 'PIX' ? {
        qrCodeText: pixCopyPaste,
        qrCodeUrl: `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(pixCopyPaste)}`,
        expiresAt: new Date(Date.now() + 30 * 60 * 1000).toISOString()
      } : null,
      trackingCode: null
    };

    // Salva nos pedidos recentes do usuário
    try {
      const recentOrders = JSON.parse(localStorage.getItem('urb_orders') || '[]');
      recentOrders.unshift(createdOrder);
      localStorage.setItem('urb_orders', JSON.stringify(recentOrders));
    } catch (e) {
      // Ignore
    }

    return createdOrder;
  }

  async getOrder(orderNumber) {
    try {
      const res = await fetch(this.endpoints.pedidoDetalhe(orderNumber), {
        method: 'GET',
        headers: this.getHeaders(true),
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (e) {
      // Backend fallback
    }

    // Busca no storage local
    try {
      const recentOrders = JSON.parse(localStorage.getItem('urb_orders') || '[]');
      const found = recentOrders.find(o => o.orderNumber === orderNumber);
      if (found) return found;
    } catch (e) {
      // Ignore
    }

    return null;
  }

  // --- REVIEWS / AVALIAÇÕES ---
  async addReview(productId, reviewData) {
    try {
      const res = await fetch(this.endpoints.avaliacoes, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify({ productId, ...reviewData }),
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (e) {
      // Backend fallback
    }
    return { success: true, message: 'Avaliação enviada com sucesso!' };
  }

  // --- ADMIN KPIS & GESTÃO ---
  async getAdminKpis() {
    try {
      const res = await fetch(this.endpoints.adminKpis, {
        method: 'GET',
        headers: this.getHeaders(true),
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (e) {
      // Backend fallback
    }

    return {
      vendasHoje: 3840.00,
      vendasMes: 48920.00,
      totalPedidos: 142,
      ticketMedio: 344.50,
      pecasVendidas: 389,
      estoqueBaixoAlertas: 3
    };
  }
}

const lojaServiceInstance = new LojaService();
export default lojaServiceInstance;
